import React, { useState, useEffect, useImperativeHandle } from 'react';
import Select from '../../../../../../../src/components/common/Select';
import InputWrapper from '../../../../../common/InputWrapper';
import Checkbox from '../../../../../common/Checkbox';
import { MARKETPLACE_ADDITIONAL_PROPERTIES } from '../../../../../../helpers';
import { fetchGetEshopOzonWarehouseList } from '../../../../../../actions/admin';
import { useDispatch } from 'react-redux';
import { AvitoDeliveryContractSelect } from './FormSelects';

const removePathFromLabel = (label) => {
  const arrOfpaths = label.split('.');
  return arrOfpaths[arrOfpaths.length - 1];
};

const generateInputLabelByPath = (path) => {
  const label = removePathFromLabel(path);
  const cleanedPath = label.replace(/\[\d+\]$/, '');
  return MARKETPLACE_ADDITIONAL_PROPERTIES[cleanedPath] || label;
};

const filterCollectedEntries = (data) => {
  const exampleObjs = data.filter((obj) => obj.path.includes('Example'));
  const nonExampleObjs = data.filter((obj) => !obj.path.includes('Example'));

  const nonExampleToDelete = new Set();

  exampleObjs.forEach((exampleObj) => {
    const originalPath = exampleObj.path.replace('Example', '');
    const sourceObj = nonExampleObjs.find((non) => non.path === originalPath);
    if (sourceObj) {
      exampleObj.value = sourceObj.value;
      nonExampleToDelete.add(sourceObj);
    } else {
      exampleObj.value = '';
    }
  });

  const remainingNonExample = nonExampleObjs.filter((non) => !nonExampleToDelete.has(non));
  const thisObjectIsTheSameAsCollected = [...exampleObjs, ...remainingNonExample];

  const result = JSON.parse(JSON.stringify(thisObjectIsTheSameAsCollected)); //copy object

  result.forEach((obj) => {
    if (obj.path.includes('Example')) {
      obj.path = obj.path.replace('Example', '');
      obj.label = obj.label.replace('Example', '');
    }

    if (obj.path.includes('warehouseIds')) {
      obj.path = 'warehouseIds';
    }
  });

  result.forEach((obj) => {
    const pathParts = obj.path.split('.');
    const deliveryMapIndex = pathParts.indexOf('deliveryInfoMap');

    const logisticMapIndex = pathParts.indexOf('logisticsServicesMap');

    if (deliveryMapIndex !== -1) {
      obj.group = pathParts[deliveryMapIndex + 1] || null;
    }

    if (logisticMapIndex !== -1) {
      obj.group = pathParts[logisticMapIndex + 1] || ' ';
    }
  });

  return result;
};

const collectEntries = (data) => {
  const result = [];
  const traverse = (data, path = '') => {
    if (typeof data === 'object' && data !== null) {
      if (Array.isArray(data)) {
        data.forEach((item, index) => {
          const newPath = path ? `${path}[${index}]` : `[${index}]`;
          traverse(item, newPath);
        });
      } else {
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            const value = data[key];
            const newPath = path ? `${path}.${key}` : key;
            traverse(value, newPath);
          }
        }
      }
    } else if (path) {
      const obj = {
        path,
        label: generateInputLabelByPath(path),
        value: data,
        type: typeof data,
      };
      result.push(obj);
    }
  };

  traverse(data);
  return result;
};

const applyChanges = (original, changes) => {
  const clone = JSON.parse(JSON.stringify(original));

  const setValue = (obj, path, value) => {
    const parts = path.split(/\.+/).filter((p) => p !== '');
    let current = obj;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLast = i === parts.length - 1;

      if (!isLast) {
        const nextPart = parts[i + 1];
        const nextIsNumeric = /^\d+$/.test(nextPart);

        if (!current[part]) {
          current[part] = nextIsNumeric ? [] : {};
        } else {
          if (nextIsNumeric && !Array.isArray(current[part])) {
            current[part] = [];
          } else if (
            !nextIsNumeric &&
            (typeof current[part] !== 'object' || Array.isArray(current[part]))
          ) {
            current[part] = {};
          }
        }

        current = current[part];
      } else {
        current[part] = value;
      }
    }
  };

  Object.entries(changes).forEach(([path, value]) => {
    setValue(clone, path, value);
  });

  return clone;
};

const GroupedInputs = ({ data, changes, handleChange, currentMarketplaceType }) => {
  const dispatch = useDispatch();
  const [ozonWarehouseOptions, setOzonWarehouseOptions] = useState(null);

  useEffect(() => {
    const handleGetOzonWarehouses = async () => {
      const warehouseList = await dispatch(fetchGetEshopOzonWarehouseList());

      if (warehouseList) {
        const ozonWarehouseOptions = warehouseList.map((w) => {
          return {
            label: w.name + ' - ' + w.warehouseId,
            value: w.warehouseId,
          };
        });

        setOzonWarehouseOptions(ozonWarehouseOptions);
      }
    };

    if (!ozonWarehouseOptions && currentMarketplaceType === 'OZON') {
      handleGetOzonWarehouses();
    }
  }, []);

  const { groupedEntries, ungroupedEntries } = data.reduce(
    (acc, entry) => {
      if (entry.group) {
        if (!acc.groupedEntries[entry.group]) {
          acc.groupedEntries[entry.group] = [];
        }
        acc.groupedEntries[entry.group].push(entry);
      } else {
        acc.ungroupedEntries.push(entry);
      }
      return acc;
    },
    { groupedEntries: {}, ungroupedEntries: [] },
  );

  const renderInputs = (entry) => {
    const value = changes[entry.path] ?? entry.value;

    if (entry.label === 'eshopType') return null;

    if (entry.type === 'boolean') {
      return (
        <div className="mr-1 mb-2" key={entry.path}>
          <Checkbox
            id={entry.path}
            checked={value ?? false}
            onChange={(e) => handleChange(entry.path, e.target.checked, entry.type)}
            label={generateInputLabelByPath(entry.label)}
          />
        </div>
      );
    }

    if (entry.label === 'warehouseIds' && currentMarketplaceType === 'BERU') {
      const inputType = entry.type === 'number' ? 'number' : 'text';
      return (
        <div className="mr-1 mb-2" key={entry.path}>
          <InputWrapper
            type={inputType}
            value={value ?? ''}
            onChange={(e) => handleChange(entry.path, e.target.value, entry.type)}
            label={generateInputLabelByPath(entry.label)}
          />
        </div>
      );
    }

    if (entry.label === 'ID склада' && currentMarketplaceType === 'OZON') {
      return (
        <div className="mr-1 mb-2">
          <Select
            label={generateInputLabelByPath(entry.label)}
            options={ozonWarehouseOptions}
            value={
              ozonWarehouseOptions?.find((o) => o.value === value) || { label: value, value: value }
            }
            onChange={({ value }) => handleChange(entry.path, value, entry.type)}
            isClearable={false}
          />
        </div>
      );
    }

    if (entry.label === 'courierDeliveryInfoId' && currentMarketplaceType === 'AVITO') {
      return (
        <AvitoDeliveryContractSelect
          entry={entry}
          value={value}
          handleChange={handleChange}
          label={generateInputLabelByPath(entry.label)}
        />
      );
    }

    if (entry.label === 'pvzDeliveryInfoId' && currentMarketplaceType === 'AVITO') {
      return (
        <AvitoDeliveryContractSelect
          entry={entry}
          value={value}
          handleChange={handleChange}
          label={generateInputLabelByPath(entry.label)}
        />
      );
    }

    if (entry.label === 'deliveryInfoId' && currentMarketplaceType === 'ALIEXPRESS') {
      return (
        <AvitoDeliveryContractSelect
          entry={entry}
          value={value}
          handleChange={handleChange}
          label={generateInputLabelByPath(entry.label)}
        />
      );
    }

    if (entry.label === 'pvzDeliveryInfoId' && currentMarketplaceType === 'ALIEXPRESS') {
      return (
        <AvitoDeliveryContractSelect
          entry={entry}
          value={value}
          handleChange={handleChange}
          label={generateInputLabelByPath(entry.label)}
        />
      );
    }

    if (entry.type === 'boolean') {
      return (
        <div className="mr-1 mb-2" key={entry.path}>
          <Checkbox
            id={entry.path}
            checked={value ?? false}
            onChange={(e) => handleChange(entry.path, e.target.checked, entry.type)}
            label={generateInputLabelByPath(entry.label)}
          />
        </div>
      );
    }

    const inputType = entry.type === 'number' ? 'number' : 'text';

    return (
      <div className="mr-1 mb-2" key={entry.path}>
        <InputWrapper
          type={inputType}
          value={value ?? ''}
          onChange={(e) => handleChange(entry.path, e.target.value, entry.type)}
          label={generateInputLabelByPath(entry.label)}
        />
      </div>
    );
  };

  const isGroupedEntriesEmpty = () => Object.keys(groupedEntries).length === 0;

  return (
    <>
      {/* Render of non-stadart input groups/complicated/table-shaped stuff */}
      {currentMarketplaceType === 'AVITO' && !isGroupedEntriesEmpty() ? (
        <>
          <h5 className="mt-0">Соответствие служб доставки Avito и Кактуса</h5>
          <div style={{ display: 'flex' }}>
            <h6 style={{ maxWidth: '100px', width: '100px' }}>{currentMarketplaceType}</h6>
            <h6 style={{ width: '33%', textAlign: 'left', marginBottom: '0', marginLeft: '12px' }}>
              kak2c (Курьер)
            </h6>
            <h6 style={{ width: '33%', textAlign: 'left', marginBottom: '0', marginLeft: '19px' }}>
              kak2с (ПВЗ)
            </h6>
          </div>
        </>
      ) : null}

      {currentMarketplaceType === 'ALIEXPRESS' && !isGroupedEntriesEmpty() ? (
        <>
          <h5 className="mt-0">
            Соответствие служб доставки AlieExpress и способов доставки в Кактусе
          </h5>
          <div style={{ display: 'flex' }}>
            <h6 style={{ maxWidth: '100px', width: '100px' }}>Служба доставки AlieExpress</h6>
            <h6 style={{ width: '33%', textAlign: 'left', marginBottom: '0', marginLeft: '12px' }}>
              Служба доставки Kak2c
            </h6>
            <h6 style={{ width: '33%', textAlign: 'left', marginBottom: '0', marginLeft: '19px' }}>
              Служба доставки Kak2с (ПВЗ)
            </h6>
          </div>
        </>
      ) : null}

      {Object.entries(groupedEntries).map(([groupName, entries]) => (
        <div key={groupName}>
          <div style={{ display: 'flex', maxWidth: '100%', alignItems: 'center' }}>
            <h6 style={{ margin: 0, maxWidth: '100px', width: '100px' }}>{groupName || ' '}</h6>
            {entries.map(renderInputs)}
          </div>
        </div>
      ))}

      {/* Render the column of regular inputs/checkboxes row by row */}
      {ungroupedEntries.length > 0 && <div>{ungroupedEntries.map(renderInputs)}</div>}
    </>
  );
};

const ObjectEditor = React.forwardRef((props, ref) => {
  const { data, currentMarketplaceType } = props;

  const [collected, setCollected] = useState([]);
  const [entries, setEntries] = useState([]);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    const collected = collectEntries(data);
    const filtred = filterCollectedEntries(collected);
    setEntries(filtred);
    setChanges({});
  }, []);

  const handleChange = (path, value, type) => {
    if (path === 'warehouseIds') {
      setChanges((prev) => ({
        ...prev,
        warehouseIds: [parseInt(value)],
      }));
      return;
    }

    setChanges((prev) => ({
      ...prev,
      [path]: type === 'boolean' ? value : value,
    }));
  };

  useImperativeHandle(ref, () => ({
    getData: () => {
      //Pushing data to MarketplaceForms.jsx component via forwardRef()
      const updated = applyChanges(data, changes);
      return updated;
    },
  }));

  return (
    <GroupedInputs
      data={entries}
      changes={changes}
      handleChange={handleChange}
      currentMarketplaceType={currentMarketplaceType}
    />
  );
});

const AdditionalForm = React.forwardRef((props, ref) => {
  const {
    currentMarketplace,
    getAdditionalPropertiesCb,
    handleSubmit,
    setUpdatedAdditionalProperties,
  } = props;

  return (
    <div>
      <ObjectEditor
        data={currentMarketplace[0]?.additionalProperties}
        currentMarketplace={currentMarketplace}
        getAdditionalPropertiesCb={getAdditionalPropertiesCb}
        handleSubmit={handleSubmit}
        setUpdatedAdditionalProperties={setUpdatedAdditionalProperties}
        ref={ref}
        currentMarketplaceType={currentMarketplace[0]?.type}
      />
    </div>
  );
});

export default AdditionalForm;
